// GestionPosMobile.tsx — Refonte mobile du Point de Vente (vue présentationnelle)
// À déposer dans : src/gestion-pos/GestionPosMobile.tsx
//
// Ce composant ne contient AUCUNE logique métier : il reçoit l'état et les handlers
// déjà présents dans GestionPos.tsx (cf. README) et rend la nouvelle UI mobile :
//   en-tête + contexte → catalogue → barre panier → feuille panier → encaissement,
//   plus les feuilles Commandes / Réimpressions et l'écran de succès (optionnels).
// Primitives (Icon, fmt, types…) dans posMobileShared.tsx ; chaque surface lourde a
// son propre fichier (PosOrdersSheet / PosReprintSheet / PosSuccessScreen).
import React, { useState } from "react";
import "./pos-mobile-kit.css";
import {
  Icon, fmt, accOf, isCashMode, iconForMode, Thumb,
  Product, CartItem, Category, Customer, PaymentMode,
  PosOrder, PosTicket, PosSaleResult,
} from "./posMobileShared";
import PosOrdersSheet from "./PosOrdersSheet";
import PosReprintSheet, { ReprintPeriod } from "./PosReprintSheet";
import PosSuccessScreen from "./PosSuccessScreen";
import MobileModuleHeader, { MobileHeaderAction } from "../mobile/MobileModuleHeader";

export interface GestionPosMobileProps {
  /* contexte */
  onMenu?: () => void;
  companyName: string;
  warehouseName: string;
  cashierName: string;
  loading?: boolean;
  catalogLoading?: boolean;
  catalogUnavailable?: boolean;
  catalogFeedback?: React.ReactNode;
  catalogActions?: React.ReactNode;
  catalogFooter?: React.ReactNode;
  /* catalogue */
  products: Product[];                 // = filteredProducts
  categories: Category[];
  activeCategory: number | null;
  onSelectCategory: (id: number | null) => void;
  searchTerm: string;
  onSearch: (term: string) => void;
  searchResults: Product[];
  showSearchResults: boolean;
  onSearchResultClick: (p: Product) => void;
  onScan: () => void;
  onAddToCart: (p: Product) => void;
  resolveImage?: (p: Product | CartItem) => string | null;
  /* panier */
  cart: CartItem[];
  cartItemCount: number;
  subtotal: number;
  discount: number;                    // = calculatedDiscount
  taxLabel?: string;                   // ex. "TVA 18 %"
  taxAmount: number;
  total: number;
  onQuantityChange: (productId: number, qty: number) => void;
  onRemoveItem: (productId: number) => void;
  onEmptyCart: () => void;
  /* client */
  selectedCustomer: number | null;
  customers: Customer[];
  onSelectCustomer: (id: number) => void;
  onQuickClient: () => void;
  canCreateClient?: boolean;
  customerSearch?: string;
  onCustomerSearch?: (query: string) => void;
  onCustomerPickerOpen?: () => void;
  customerLoading?: boolean;
  customerFeedback?: React.ReactNode;
  customerMoreFeedback?: React.ReactNode;
  onCustomerScroll?: React.UIEventHandler<HTMLDivElement>;
  /* paiement */
  paymentModes: PaymentMode[];
  selectedModePaiement: number | null;
  onSelectMode: (id: number) => void;
  amountReceived: number;
  onAmountReceived: (n: number) => void;
  changeAmount: number;
  onStartPayment?: () => void;
  invoiceNumberField?: React.ReactNode;
  onSubmit: () => void;
  submitting?: boolean;
  canSubmit?: boolean;
  /* en-tête : actions secondaires */
  onOpenOrders?: () => void;     // délègue au drawer restaurant existant
  ordersBadge?: number;          // = restaurantAttentionCount
  onOpenReprint?: () => void;    // délègue à PosReprintCenter existant
  /* OPTION — feuille « Commandes » mobile native (sinon onOpenOrders délègue) :
     fournis `orders` pour afficher la feuille mobile au lieu du drawer desktop. */
  orders?: PosOrder[];
  onSelectOrder?: (o: PosOrder) => void;
  /* OPTION — feuille « Réimpressions » mobile native (sinon onOpenReprint délègue) :
     fournis `tickets` + les callbacks d'action. */
  tickets?: PosTicket[];
  reprintPeriod?: ReprintPeriod;
  onReprintPeriodChange?: (p: ReprintPeriod) => void;
  onReprintSearch?: (term: string) => void;
  onReprintTicket?: (t: PosTicket) => void;
  onDownloadTicketPdf?: (t: PosTicket) => void;
  onAddTicketPayment?: (t: PosTicket) => void;
  /* OPTION — écran de succès mobile (sinon garde ton <TicketCaisse />) :
     fournis `lastSale` après une vente réussie pour l'afficher. */
  lastSale?: PosSaleResult | null;
  onCloseSale?: () => void;
  onPrintTicket?: () => void;
  /* serveurs (optionnel, profils restauration uniquement) */
  serversEnabled?: boolean;
  servers?: { id: number; name: string }[];
  selectedServerId?: number | null;
  onSelectServer?: (id: number | null) => void;
  onManageServers?: () => void;
}

/* ═══════════════════════════════════════════════════════════ */
export default function GestionPosMobile(props: GestionPosMobileProps) {
  const {
    onMenu, companyName, warehouseName, cashierName, loading,
    catalogLoading, catalogUnavailable, catalogFeedback, catalogActions, catalogFooter,
    products, categories, activeCategory, onSelectCategory,
    searchTerm, onSearch, searchResults, showSearchResults, onSearchResultClick,
    onScan, onAddToCart, resolveImage,
    cart, cartItemCount, subtotal, discount, taxLabel = "TVA incluse", taxAmount, total,
    onQuantityChange, onRemoveItem, onEmptyCart,
    selectedCustomer, customers, onSelectCustomer, onQuickClient,
    canCreateClient = true,
    customerSearch, onCustomerSearch, onCustomerPickerOpen, customerLoading, customerFeedback, customerMoreFeedback, onCustomerScroll,
    paymentModes, selectedModePaiement, onSelectMode,
    amountReceived, onAmountReceived, changeAmount, onStartPayment,
    invoiceNumberField,
    onSubmit, submitting, canSubmit,
    onOpenOrders, ordersBadge = 0, onOpenReprint,
    orders, onSelectOrder,
    tickets, reprintPeriod, onReprintPeriodChange, onReprintSearch,
    onReprintTicket, onDownloadTicketPdf, onAddTicketPayment,
    lastSale, onCloseSale, onPrintTicket,
    serversEnabled = false, servers = [], selectedServerId = null,
    onSelectServer, onManageServers,
  } = props;

  const [sheetOpen, setSheetOpen] = useState(false);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [clientQuery, setClientQuery] = useState("");
  const [view, setView] = useState<"catalog" | "payment">("catalog");
  const [tool, setTool] = useState<null | "orders" | "reprint">(null);

  const img = (p: Product | CartItem) => (resolveImage ? resolveImage(p) : p.image || null);
  const selectedCustomerName =
    customers.find((c) => c.ID_Client === selectedCustomer)?.Nom_Raison_Sociale || "";
  const customerName = selectedCustomerName || "Client comptoir";
  const visibleCustomers = onCustomerSearch ? customers : customers.filter((customer) =>
    customer.Nom_Raison_Sociale.toLowerCase().includes(clientQuery.trim().toLowerCase())
  );
  const activeMode = paymentModes.find((m) => m.id === selectedModePaiement) || null;
  const catLabel =
    activeCategory === null ? "Tous"
      : categories.find((c) => c.id === activeCategory)?.categorie || "Catégorie";

  /* en-tête : feuille mobile native si data fournie, sinon délégation desktop */
  const showOrdersBtn = !!(orders || onOpenOrders);
  const showReprintBtn = !!(tickets || onOpenReprint);
  const ordersCount = ordersBadge || (orders ? orders.filter((o) => o.status !== "kitchen").length : 0);
  const handleOrders = () => (orders ? setTool("orders") : onOpenOrders && onOpenOrders());
  const handleReprint = () => (tickets ? setTool("reprint") : onOpenReprint && onOpenReprint());

  /* pavé espèces */
  const due = total;
  const cash = amountReceived;
  const cashOk = cash >= due;
  const press = (d: number) => onAmountReceived(Math.min(cash * 10 + d, 9_9999_9999));
  const validable = !!canSubmit && (!isCashMode(activeMode) || cashOk);

  return (
    <div className="pos-m">
      <MobileModuleHeader
        title="Point de vente"
        subtitle={companyName}
        onMenu={onMenu}
        actions={
          <>
            {showOrdersBtn ? (
              <MobileHeaderAction label="Commandes" onClick={handleOrders} badge={ordersCount}>
                <Icon name="receipt" size={20} stroke={1.8} />
              </MobileHeaderAction>
            ) : null}
            {showReprintBtn ? (
              <MobileHeaderAction label="Réimpressions" onClick={handleReprint}>
                <Icon name="printer" size={20} stroke={1.8} />
              </MobileHeaderAction>
            ) : null}
          </>
        }
        context={<div className="pos-m-context">
          <span className="pos-ctx-chip is-live"><span className="dot" />{warehouseName}</span>
          <span className="pos-ctx-chip"><Icon name="user" size={14} stroke={1.9} style={{ color: "var(--rf-muted)" }} />{cashierName}</span>
          <span className="pos-ctx-chip"><Icon name="clock" size={14} stroke={1.9} style={{ color: "var(--rf-muted)" }} />Caisse ouverte</span>
        </div>}
      >
        <div className="pos-m-searchrow">
          <div className="pos-m-search" style={{ position: "relative" }}>
            <Icon name="search" size={19} stroke={2} />
            <input placeholder="Rechercher un produit…" value={searchTerm}
              maxLength={120}
              onChange={(e) => onSearch(e.target.value)} />
            {showSearchResults && searchResults.length > 0 && (
              <div className="pos-search-pop">
                {searchResults.slice(0, 6).map((p) => (
                  <button key={p.id_produit} className="pos-search-item"
                    onClick={() => onSearchResultClick(p)}>
                    <span>{p.nom_produit}</span>
                    <b>{fmt(p.prix_vente)} F</b>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button className="pos-m-scan" onClick={onScan}><Icon name="scan" size={22} stroke={1.9} /></button>
        </div>
      </MobileModuleHeader>

      {/* ═══ CATALOGUE ═══ */}
      <div className="pos-m-body">
        <div className="pos-m-cats">
          <button className={"pos-cat" + (activeCategory === null ? " is-active" : "")}
            onClick={() => onSelectCategory(null)}>Tous</button>
          {categories.map((c) => (
            <button key={c.id} className={"pos-cat" + (activeCategory === c.id ? " is-active" : "")}
              onClick={() => onSelectCategory(c.id)}>{c.categorie}</button>
          ))}
        </div>
        <div className="pos-m-catmeta">{products.length} articles affichés · {catLabel}</div>
        {catalogFeedback}
        {catalogActions}
        {(loading || catalogLoading) && products.length === 0 ? (
          <div className="pos-empty"><div className="pos-empty-t">Chargement…</div></div>
        ) : products.length === 0 ? (
          <div className="pos-empty">
            <div className="pos-empty-ico"><Icon name="box" size={30} stroke={1.6} /></div>
            <div className="pos-empty-t">{catalogUnavailable ? "Catalogue indisponible" : "Aucun produit dans les choix chargés"}</div>
            <div className="pos-empty-s">{catalogUnavailable ? "Actualisez le catalogue pour charger les articles." : "Sélectionnez un établissement ou changez de catégorie."}</div>
          </div>
        ) : (
          <div className="pos-m-grid">
            {products.map((p) => {
              const inCart = cart.find((c) => c.id_produit === p.id_produit)?.cartQuantity || 0;
              const low = p.item_type !== "service" && p.quantite_stock != null && p.quantite_stock <= 20;
              return (
                <button key={p.id_produit} className={"pos-card" + (inCart ? " in-cart" : "")}
                  aria-label={`Ajouter ${p.nom_produit} au panier`}
                  onClick={() => onAddToCart(p)}>
                  {inCart > 0 && <span className="pos-card-qty">{inCart}</span>}
                  <div className="pos-card-thumb" style={{ background: accOf(p.id_produit).bg }}>
                    <span className={"pos-card-stock" + (low ? " is-low" : "")}>
                      {low ? <Icon name="alert" size={11} stroke={2.4} /> : null}
                      {p.item_type === "service"
                        ? `Service${p.duration_minutes ? ` · ${p.duration_minutes} min` : ""}`
                        : p.quantite_stock == null ? "Stock à vérifier" : `${p.quantite_stock} en stock`}
                    </span>
                    <Thumb product={p} url={img(p)} size={36} />
                  </div>
                  <div className="pos-card-name">{p.nom_produit}</div>
                  <div className="pos-card-foot">
                    <div className="pos-card-price">{fmt(p.prix_vente)}<span className="cfa">F</span></div>
                    <span className="pos-card-add"><Icon name="plus" size={19} stroke={2.4} /></span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {catalogFooter}
      </div>

      {/* ═══ BARRE PANIER PERMANENTE ═══ */}
      <div className="pos-m-cartbar">
        <button className={"pos-cartbar-btn" + (cartItemCount === 0 ? " is-empty" : "")}
          aria-label={cartItemCount > 0 ? "Ouvrir le panier" : "Panier vide"}
          onClick={() => cartItemCount > 0 && setSheetOpen(true)}>
          <span className="pos-cartbar-ico">
            <Icon name="cart" size={20} stroke={1.9} />
            {cartItemCount > 0 && <span className="pos-cartbar-count">{cartItemCount}</span>}
          </span>
          <span className="pos-cartbar-mid">
            <span className="pos-cartbar-l1">{cartItemCount === 0 ? "Panier vide" : "Total panier"}</span>
            <span className="pos-cartbar-l2">{cartItemCount === 0 ? "Touchez un produit" : fmt(total) + " F"}</span>
          </span>
          {cartItemCount > 0 && <span className="pos-cartbar-cta">Voir<Icon name="chevronUp" size={16} stroke={2.4} /></span>}
        </button>
      </div>

      {/* ═══ FEUILLE PANIER ═══ */}
      {sheetOpen && cartItemCount > 0 && (
        <>
          <div className="pos-scrim" onClick={() => { setClientPickerOpen(false); setSheetOpen(false); }} />
          <div className="pos-sheet">
            <div className="pos-grab" />
            <div className="pos-sheet-head">
              <div className="pos-sheet-title">Panier<span className="n">{cartItemCount} article{cartItemCount > 1 ? "s" : ""}</span></div>
              <button className="pos-sheet-close" onClick={() => { setClientPickerOpen(false); setSheetOpen(false); }}><Icon name="x" size={18} stroke={2.2} /></button>
            </div>
            <div className="pos-sheet-body">
              {/* client */}
              <div className="pos-client">
                <div className="pos-client-ico"><Icon name="user" size={20} stroke={1.9} /></div>
                <div className="pos-client-mid">
                  <div className="pos-client-l">Client</div>
                  <button className={"pos-client-trigger" + (selectedCustomerName ? "" : " is-empty")}
                    aria-label="Choisir le client"
                    onClick={() => { setClientPickerOpen(true); onCustomerPickerOpen?.(); }}>
                    <span className="pos-client-trigger-label">{selectedCustomerName || "Sélectionner un client"}</span>
                    <Icon name="chevron" size={16} stroke={2.2} />
                  </button>
                </div>
                <button className="pos-client-go" disabled={!canCreateClient} onClick={onQuickClient}>
                  <Icon name="plus" size={15} stroke={2.4} />Nouveau
                </button>
              </div>

              {/* lignes */}
              {cart.map((item) => {
                const acc = accOf(item.id_produit);
                return (
                  <div className="pos-line" key={item.id_produit}>
                    <div className="pos-line-thumb" style={{ overflow: "hidden", background: acc.bg }}>
                      <Thumb product={item} url={img(item)} size={24} />
                    </div>
                    <div className="pos-line-mid">
                      <div className="pos-line-name">{item.nom_produit}</div>
                      <div className="pos-line-price" role="status" aria-label={`Prix unitaire de ${item.nom_produit}`}>{fmt(item.prix_vente)} F <b>· {item.cartQuantity}</b></div>
                    </div>
                    <div className="pos-line-right">
                      <div className="pos-line-total">{fmt(item.prix_vente * item.cartQuantity)} F</div>
                      <div className="pos-stepper">
                        <button className={"pos-step-btn" + (item.cartQuantity <= 1 ? " is-trash" : "")}
                          aria-label={`Retirer une unité de ${item.nom_produit}`}
                          onClick={() => item.cartQuantity <= 1 ? onRemoveItem(item.id_produit) : onQuantityChange(item.id_produit, item.cartQuantity - 1)}>
                          <Icon name={item.cartQuantity <= 1 ? "trash" : "minus"} size={16} stroke={2.2} />
                        </button>
                        <span className="pos-step-val" role="status" aria-label={`Quantité saisie pour ${item.nom_produit}`}>{item.cartQuantity}</span>
                        <button className="pos-step-btn" aria-label={`Ajouter une unité de ${item.nom_produit}`} onClick={() => onQuantityChange(item.id_produit, item.cartQuantity + 1)}>
                          <Icon name="plus" size={16} stroke={2.2} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              <button className="pos-cart-clear" onClick={onEmptyCart}>
                <Icon name="trash" size={16} stroke={2} />Vider le panier
              </button>

              {/* totaux */}
              <div className="pos-totals">
                <div className="pos-trow"><span className="l"><Icon name="cart" size={16} stroke={1.9} style={{ color: "var(--rf-muted)" }} />Sous-total</span><span className="v">{fmt(subtotal)} F</span></div>
                {discount > 0 && (
                  <div className="pos-trow disc"><span className="l"><Icon name="percent" size={16} stroke={1.9} style={{ color: "var(--rf-green-d)" }} />Remise</span><span className="v">− {fmt(discount)} F</span></div>
                )}
                <div className="pos-trow"><span className="l"><Icon name="info" size={16} stroke={1.9} style={{ color: "var(--rf-muted)" }} />{taxLabel}</span><span className="v">{fmt(taxAmount)} F</span></div>
                <div className="pos-tdiv" />
                <div className="pos-ttotal"><span className="l">Total à payer</span><span className="v">{fmt(total)}<span className="cfa">F</span></span></div>
              </div>
            </div>
            <div className="pos-sheet-foot">
              <button className="pos-pay-btn" onClick={() => { onStartPayment?.(); setSheetOpen(false); setView("payment"); }}>
                <Icon name="arrowRight" size={20} stroke={2.2} />
                Encaisser · <span className="amt">{fmt(total)} F</span>
              </button>
            </div>
          </div>
        </>
      )}

      {clientPickerOpen && (
        <>
          <div className="pos-client-picker-scrim" onClick={() => setClientPickerOpen(false)} />
          <div className="pos-client-picker">
            <div className="pos-grab" />
            <div className="pos-client-picker-head">
              <div className="pos-client-picker-title">Sélectionner un client</div>
              <button className="pos-sheet-close" onClick={() => setClientPickerOpen(false)}>
                <Icon name="x" size={18} stroke={2.2} />
              </button>
            </div>
            <label className="pos-client-picker-search">
              <Icon name="search" size={18} stroke={2} />
              <input
                autoFocus
                value={customerSearch ?? clientQuery}
                onChange={(event) => { setClientQuery(event.target.value); onCustomerSearch?.(event.target.value); }}
                maxLength={120}
                placeholder="Rechercher un client…"
              />
            </label>
            {customerFeedback}
            <div className="pos-client-picker-list" onScroll={onCustomerScroll}>
              {visibleCustomers.length === 0 ? (
                <div className="pos-client-picker-empty">{customerLoading ? "Chargement des clients…" : "Aucun client dans les choix chargés."}</div>
              ) : visibleCustomers.map((customer) => {
                const selected = customer.ID_Client === selectedCustomer;
                return (
                  <button
                    key={customer.ID_Client}
                    className={"pos-client-option" + (selected ? " is-selected" : "")}
                    onClick={() => {
                      onSelectCustomer(customer.ID_Client);
                      setClientPickerOpen(false);
                    }}
                  >
                    <span className="pos-client-option-ico">
                      <Icon name="user" size={17} stroke={2} />
                    </span>
                    <span className="pos-client-option-name">{customer.Nom_Raison_Sociale}</span>
                    {selected && <Icon name="check" size={18} stroke={2.3} />}
                  </button>
                );
              })}
            </div>
            {customerMoreFeedback}
          </div>
        </>
      )}

      {/* ═══ COMMANDES (feuille mobile native, optionnelle) ═══ */}
      {tool === "orders" && orders && (
        <PosOrdersSheet orders={orders} badge={ordersBadge || undefined}
          onSelectOrder={onSelectOrder} onClose={() => setTool(null)} />
      )}

      {/* ═══ RÉIMPRESSIONS (feuille mobile native, optionnelle) ═══ */}
      {tool === "reprint" && tickets && (
        <PosReprintSheet tickets={tickets} period={reprintPeriod}
          onPeriodChange={onReprintPeriodChange} onSearch={onReprintSearch}
          onReprint={onReprintTicket} onDownloadPdf={onDownloadTicketPdf}
          onAddPayment={onAddTicketPayment} onClose={() => setTool(null)} />
      )}

      {/* ═══ ENCAISSEMENT (plein écran) ═══ */}
      {view === "payment" && (
        <div className="pos-pay">
          <div className="pos-pay-head">
            <button className="pos-pay-back" onClick={() => { setView("catalog"); setSheetOpen(true); }}>
              <Icon name="back" size={20} stroke={2.2} />
            </button>
            <div>
              <div className="pos-pay-htitle">Encaissement</div>
              <div className="pos-pay-hsub">{cartItemCount} article{cartItemCount > 1 ? "s" : ""} · {customerName}</div>
            </div>
          </div>

          <div className="pos-pay-body">
            {invoiceNumberField}
            <div className="pos-due">
              <div className="pos-due-l">Montant à payer</div>
              <div className="pos-due-v">{fmt(due)}<span className="cfa">F</span></div>
              <div className="pos-due-meta">
                <Icon name="store" size={14} stroke={1.9} /> {warehouseName}
                <span className="sep">·</span>
                <Icon name="user" size={14} stroke={1.9} /> {cashierName}
              </div>
            </div>

            {serversEnabled && (
              <>
                <div
                  className="pos-seclbl"
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <span>Serveur (optionnel)</span>
                  {onManageServers && (
                    <button
                      onClick={onManageServers}
                      style={{ background: "none", border: "none", color: "var(--rf-primary)", fontSize: 13, cursor: "pointer", padding: 0 }}
                    >
                      Gérer
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                  {[{ id: null as number | null, name: "Aucun" }, ...servers].map((s) => {
                    const active = selectedServerId === s.id;
                    return (
                      <button
                        key={s.id ?? "none"}
                        onClick={() => onSelectServer?.(s.id)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: 999,
                          border: active ? "1px solid var(--rf-primary)" : "1px solid #d9d9d9",
                          background: active ? "var(--rf-primary)" : "#fff",
                          color: active ? "#fff" : "#333",
                          fontSize: 13,
                          cursor: "pointer",
                        }}
                      >
                        {s.name}
                      </button>
                    );
                  })}
                  {servers.length === 0 && (
                    <span style={{ fontSize: 12, color: "#999" }}>
                      Aucun serveur — touchez « Gérer » pour en ajouter.
                    </span>
                  )}
                </div>
              </>
            )}

            <div className="pos-seclbl">Mode de paiement</div>
            <div className="pos-methods">
              {paymentModes.map((m) => (
                <button key={m.id} className={"pos-method" + (selectedModePaiement === m.id ? " is-active" : "")}
                  onClick={() => onSelectMode(m.id)}>
                  <div className="pos-method-ico" style={{ background: accOf(m.id).bg, color: accOf(m.id).fg }}><Icon name={iconForMode(m.name)} size={20} stroke={1.9} /></div>
                  <div className="pos-method-mid">
                    <div className="pos-method-l">{m.name}</div>
                  </div>
                </button>
              ))}
            </div>

            {isCashMode(activeMode) ? (
              <>
                {cash > 0 && (
                  <div className={"pos-change" + (cashOk ? "" : " is-short")}>
                    <div className="pos-change-ico"><Icon name={cashOk ? "wallet" : "alert"} size={21} stroke={1.9} /></div>
                    <div>
                      <div className="pos-change-l">{cashOk ? "Monnaie à rendre" : "Reste à encaisser"}</div>
                      <div className="pos-change-v">{fmt(cashOk ? changeAmount : due - cash)} F</div>
                    </div>
                  </div>
                )}
                <div className="pos-seclbl">Montant reçu</div>
                <div className="pos-cashbox">
                  <div className="pos-cash-display">
                    <span className="pos-cash-l">Reçu</span>
                    <span className={"pos-cash-v" + (cash === 0 ? " is-placeholder" : "")}>{fmt(cash)}<span className="cfa">F</span></span>
                  </div>
                  <div className="pos-quickcash">
                    {[2000, 5000, 10000].map((q) => (
                      <button key={q} className="pos-qc" onClick={() => onAmountReceived(q)}>{fmt(q)}</button>
                    ))}
                    <button className="pos-qc is-exact" onClick={() => onAmountReceived(due)}>Appoint</button>
                  </div>
                  <div className="pos-keypad">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((k) => (
                      <button key={k} className="pos-key" onClick={() => press(k)}>{k}</button>
                    ))}
                    <button className="pos-key is-fn" onClick={() => onAmountReceived(0)}>C</button>
                    <button className="pos-key" onClick={() => press(0)}>0</button>
                    <button className="pos-key is-fn" onClick={() => onAmountReceived(Math.floor(cash / 10))}><Icon name="back" size={20} stroke={2.2} /></button>
                  </div>
                </div>
              </>
            ) : (
              <div className="pos-change">
                <div className="pos-change-ico" style={{ background: "var(--rf-primary)" }}><Icon name="check" size={21} stroke={2.2} /></div>
                <div>
                  <div className="pos-change-l" style={{ color: "var(--rf-primary)" }}>Montant exact</div>
                  <div className="pos-change-v" style={{ color: "var(--rf-primary-d)" }}>{fmt(due)} F</div>
                </div>
              </div>
            )}
          </div>

          <div className="pos-pay-foot">
            <button className="pos-confirm" disabled={!validable || submitting} onClick={onSubmit}>
              <Icon name="checkCircle" size={21} stroke={1.9} />
              {submitting ? "Validation…" : "Valider la vente"}
            </button>
          </div>
        </div>
      )}

      {/* ═══ SUCCÈS (écran mobile, optionnel — sinon ton TicketCaisse) ═══ */}
      {lastSale && (
        <PosSuccessScreen sale={lastSale} warehouseName={warehouseName}
          onNew={() => { setView("catalog"); setSheetOpen(false); onCloseSale && onCloseSale(); }}
          onPrint={onPrintTicket} />
      )}
    </div>
  );
}
